<template>
  <v-card class="viz" v-if="logs" :style="{ height: (height > 0 ? height + 'px' : '70vh') }">
    <div class="viz-body">
      <!-- Navigation -->
      <nav class="viz-nav">
        <button class="viz-navitem" :class="{ on: view === 'home' }" @click="view = 'home'">
          <v-icon size="18">mdi-view-dashboard-outline</v-icon> Accueil
        </button>
        <div class="viz-navgroup">Parcours</div>
        <button class="viz-navitem" :class="{ on: view === 'timeline' }" @click="view = 'timeline'">
          <v-icon size="18">mdi-timeline-clock-outline</v-icon> Timeline de session
        </button>
        <button class="viz-navitem" :class="{ on: view === 'flux' }" @click="view = 'flux'">
          <v-icon size="18">mdi-sitemap</v-icon> Flux de navigation
        </button>
        <button class="viz-navitem" :class="{ on: view === 'swim' }" @click="view = 'swim'">
          <v-icon size="18">mdi-format-align-justify</v-icon> Swimlanes
        </button>
        <div class="viz-navgroup">Diagnostic</div>
        <button class="viz-navitem" :class="{ on: view === 'err' }" @click="view = 'err'">
          <v-icon size="18">mdi-alert-circle-outline</v-icon> Erreurs &amp; santé
        </button>
        <button class="viz-navitem" :class="{ on: view === 'perf' }" @click="view = 'perf'">
          <v-icon size="18">mdi-speedometer</v-icon> Performances
        </button>
        <button class="viz-navitem" :class="{ on: view === 'act' }" @click="view = 'act'">
          <v-icon size="18">mdi-chart-bar</v-icon> Activité (temps)
        </button>
        <div class="viz-navgroup">Métier</div>
        <button class="viz-navitem" :class="{ on: view === 'funnel' }" @click="view = 'funnel'">
          <v-icon size="18">mdi-filter-variant</v-icon> Entonnoir &amp; tops
        </button>
      </nav>

      <!-- Contenu -->
      <section class="viz-content" :class="{ 'viz-content-tl': view === 'timeline' }">
        <!-- légende commune -->
        <div class="viz-legend" v-if="view !== 'home'">
          <span class="it"><i class="dot" :style="{ background: C.page }"></i>page</span>
          <span class="it"><i class="dot" :style="{ background: C.selection }"></i>selection</span>
          <span class="it"><i class="dot" :style="{ background: C.api }"></i>api</span>
          <span class="it"><i class="dot" :style="{ background: C.err }"></i>erreur</span>
          <span class="it muted">· basé sur les logs affichés (filtres en cours)</span>
        </div>

        <!-- ACCUEIL / LANCEUR -->
        <div v-if="view === 'home'">
          <h2 class="viz-h2">Par où commencer ?</h2>
          <p class="viz-sub">Choisis l'angle selon le problème rencontré. Toutes les vues utilisent les logs actuellement affichés.</p>
          <div class="viz-cards">
            <button class="viz-card" @click="view = 'timeline'">
              <div class="ic" :style="{ background: C.api }"><v-icon color="white">mdi-timeline-clock-outline</v-icon></div>
              <b>Suivre un utilisateur</b>
              <small>« Qu'a fait cet utilisateur, dans quel ordre ? » → Timeline de session (et Swimlanes pour comparer).</small>
            </button>
            <button class="viz-card" @click="view = 'flux'">
              <div class="ic" :style="{ background: C.page }"><v-icon color="white">mdi-sitemap</v-icon></div>
              <b>Comprendre les chemins</b>
              <small>« Par où passent-ils globalement ? » → Flux de navigation (transitions les plus fréquentes).</small>
            </button>
            <button class="viz-card" @click="view = 'err'">
              <div class="ic" :style="{ background: C.err }"><v-icon color="white">mdi-alert-circle-outline</v-icon></div>
              <b>Quelque chose plante</b>
              <small>« Qu'est-ce qui échoue, et quand ? » → Erreurs &amp; santé (top routes en échec, pics).</small>
            </button>
            <button class="viz-card" @click="view = 'perf'">
              <div class="ic" :style="{ background: C.selection }"><v-icon color="white">mdi-speedometer</v-icon></div>
              <b>C'est lent</b>
              <small>« Quels endpoints sont lents ? » → Performances (médiane + p95 par route).</small>
            </button>
            <button class="viz-card" @click="view = 'act'">
              <div class="ic" :style="{ background: '#7E57C2' }"><v-icon color="white">mdi-chart-bar</v-icon></div>
              <b>Pic / incident / quand</b>
              <small>« À quel moment ça se passe ? » → Activité dans le temps (volume par tranche).</small>
            </button>
            <button class="viz-card" @click="view = 'funnel'">
              <div class="ic" :style="{ background: '#26A69A' }"><v-icon color="white">mdi-filter-variant</v-icon></div>
              <b>Abandons / qui-quoi</b>
              <small>« Où abandonnent-ils ? Qui génère le trafic ? » → Entonnoir &amp; classements.</small>
            </button>
          </div>
        </div>

        <!-- TIMELINE -->
        <div v-else-if="view === 'timeline'" class="viz-tlwrap">
          <h2 class="viz-h2">Timeline de session</h2>
          <div class="viz-controls">
            <v-select
              v-model="selectedSessionId"
              :items="sessionItems"
              item-title="label"
              item-value="id"
              label="Session"
              variant="outlined" density="compact" hide-details
              style="max-width:420px"
              prepend-inner-icon="mdi-account-clock-outline"
            ></v-select>
            <label class="viz-toggle"><input type="checkbox" v-model="hideApi"> masquer les <b :style="{color:C.api}">api</b></label>
          </div>
          <div v-if="selectedSession" class="viz-tl">
            <template v-for="(st, i) in tlSteps" :key="i">
              <div v-if="i > 0" class="viz-gap">↓ +{{ st.gap }}</div>
              <div class="viz-step" :class="{ sel: selStep === i }" @click="selStep = i; $emit('open-detail', st.raw)">
                <span class="nd" :style="{ background: st.isErr ? C.err : st.color }"></span>
                <div class="r1">
                  <span class="tt" :style="{ background: st.color }">{{ st.type }}</span>
                  <span class="rt">{{ st.route }}</span>
                  <span v-if="st.statut" class="sc" :style="{ background: st.scolor }">{{ st.statut }}</span>
                  <span v-if="st.isErr" class="errtag">⚠ erreur</span>
                </div>
                <div class="r2"><span>🕒 {{ st.clock }}</span><span v-if="st.dur">⏱ {{ st.dur }} ms</span></div>
              </div>
            </template>
          </div>
          <div v-else class="viz-empty">Aucune session dans les logs affichés.</div>
          <div v-if="selStepData" class="viz-detail">
            <div><span class="k">Session</span><b>{{ selectedSession.id }}</b></div>
            <div><span class="k">Utilisateur</span><b>{{ selectedSession.user || '—' }}</b></div>
            <div><span class="k">Requête</span><b>{{ selStepData.route }}</b></div>
            <div><span class="k">Statut</span><b :style="{color:selStepData.scolor}">{{ selStepData.statut || '—' }}</b></div>
            <div><span class="k">Durée</span><b>{{ selStepData.dur ? selStepData.dur + ' ms' : '—' }}</b></div>
            <div v-if="selStepData.raw.erreur_message"><span class="k">Erreur</span><b style="color:#EF5350">{{ selStepData.raw.erreur_message }}</b></div>
          </div>
        </div>

        <!-- FLUX -->
        <div v-else-if="view === 'flux'">
          <h2 class="viz-h2">Flux de navigation</h2>
          <p class="viz-sub">Transitions d'une route à la suivante (au sein des sessions). Choisis la représentation.</p>
          <div class="viz-controls">
            <div class="viz-seg">
              <button :class="{ on: fluxMode==='sankey' }" @click="fluxMode='sankey'">Sankey</button>
              <button :class="{ on: fluxMode==='top' }" @click="fluxMode='top'">Top transitions</button>
              <button :class="{ on: fluxMode==='drill' }" @click="fluxMode='drill'">Drilldown</button>
              <button :class="{ on: fluxMode==='matrix' }" @click="fluxMode='matrix'">Matrice</button>
            </div>
          </div>

          <!-- Sankey -->
          <div v-if="fluxMode==='sankey'">
            <p class="viz-sub">Colonnes par étape, rubans proportionnels au nombre de passages. Survole un ruban.</p>
            <div v-if="fluxSankey.nodes.length" class="viz-svgwrap">
              <svg :width="fluxSankey.width" :height="fluxSankey.height" :viewBox="'0 0 '+fluxSankey.width+' '+fluxSankey.height">
                <path v-for="(r, i) in fluxSankey.ribbons" :key="'r'+i" :d="r.d" :fill="r.color" class="viz-ribbon"
                  @mousemove="tipAt($event, r.title)" @mouseleave="hideTip"></path>
                <g v-for="(n, i) in fluxSankey.nodes" :key="'sn'+i">
                  <rect :x="n.x" :y="n.y" :width="n.w" :height="n.h" rx="3" :fill="n.color"></rect>
                  <text :x="n.x + n.w + 6" :y="n.ty" class="viz-sknode">{{ n.short }}</text>
                </g>
              </svg>
            </div>
            <div v-else class="viz-empty">Pas assez de transitions.</div>
          </div>

          <!-- Top transitions -->
          <div v-else-if="fluxMode==='top'">
            <p class="viz-sub">« Après X → Y, N fois », trié par fréquence.</p>
            <div v-if="fluxTop.length">
              <div v-for="(e, i) in fluxTop" :key="i" class="viz-tr"
                   @mousemove="tipAt($event, e.from+' → '+e.to+' : '+e.count)" @mouseleave="hideTip">
                <span class="pair">
                  <i class="cd" :style="{background:routeColor(e.from)}"></i><span class="rl" :title="e.from">{{ e.fromShort }}</span>
                  <span class="ar">→</span>
                  <i class="cd" :style="{background:routeColor(e.to)}"></i><span class="rl" :title="e.to">{{ e.toShort }}</span>
                </span>
                <span class="tk"><span class="fl" :style="{width:e.barW+'%', background:routeColor(e.to)}"></span></span>
                <span class="vv">{{ e.count }}</span>
              </div>
            </div>
            <div v-else class="viz-empty">Aucune transition.</div>
          </div>

          <!-- Drilldown -->
          <div v-else-if="fluxMode==='drill'">
            <p class="viz-sub">Clique une route : ce qui précède (gauche) et ce qui suit (droite).</p>
            <div class="viz-ddpick">
              <button v-for="(n, i) in fluxDrill.nodesSorted" :key="i" class="viz-ddchip"
                      :class="{ on: n===fluxDrill.selected }" :title="n" @click="fluxSel=n">{{ shortLabel(n, 24) }}</button>
            </div>
            <div class="viz-ddgrid">
              <div>
                <div class="viz-blocklabel">← Précède</div>
                <div v-for="(x, i) in fluxDrill.before" :key="i" class="viz-ddrow">
                  <i class="cd" :style="{background:routeColor(x.r)}"></i><span class="rl" :title="x.r">{{ shortLabel(x.r,26) }}</span>
                  <span class="bar"><i :style="{width:x.barW+'%', background:routeColor(x.r)}"></i></span><span class="vv">{{ x.count }}</span>
                </div>
                <div v-if="!fluxDrill.before.length" class="viz-empty sm">—</div>
              </div>
              <div class="viz-ddcenter"><div class="viz-ddnode">{{ shortLabel(fluxDrill.selected, 22) }}</div></div>
              <div>
                <div class="viz-blocklabel">Suit →</div>
                <div v-for="(x, i) in fluxDrill.after" :key="i" class="viz-ddrow">
                  <i class="cd" :style="{background:routeColor(x.r)}"></i><span class="rl" :title="x.r">{{ shortLabel(x.r,26) }}</span>
                  <span class="bar"><i :style="{width:x.barW+'%', background:routeColor(x.r)}"></i></span><span class="vv">{{ x.count }}</span>
                </div>
                <div v-if="!fluxDrill.after.length" class="viz-empty sm">—</div>
              </div>
            </div>
          </div>

          <!-- Matrice -->
          <div v-else-if="fluxMode==='matrix'">
            <p class="viz-sub">Ligne = de, colonne = vers. Case plus foncée = plus fréquent. Survole une case.</p>
            <div v-if="fluxMatrix.ord.length" class="viz-svgwrap">
              <table class="viz-mx">
                <tr><th class="corner"></th><th v-for="(c, i) in fluxMatrix.ord" :key="i" class="col" :title="c">{{ shortLabel(c,16) }}</th></tr>
                <tr v-for="(row, i) in fluxMatrix.rows" :key="i">
                  <th class="row" :title="row.from">{{ shortLabel(row.from,24) }}</th>
                  <td v-for="(cell, j) in row.cells" :key="j">
                    <div class="cell" :style="{background:cell.bg, color:cell.fg}"
                         @mousemove="tipAt($event, row.from+' → '+cell.to+' : '+cell.count)" @mouseleave="hideTip">{{ cell.count || '' }}</div>
                  </td>
                </tr>
              </table>
            </div>
            <div v-else class="viz-empty">Aucune transition.</div>
          </div>
        </div>

        <!-- SWIMLANES -->
        <div v-else-if="view === 'swim'">
          <h2 class="viz-h2">Swimlanes multi-sessions</h2>
          <p class="viz-sub">Une ligne par session, événements sur un axe de temps relatif (début = 0). Survole un point.</p>
          <div v-for="(l, i) in swimGeo.lanes" :key="i" class="viz-lane">
            <div class="lab"><b>{{ l.short }}</b><small>{{ l.user }}</small></div>
            <div class="track">
              <div v-for="(d, j) in l.dots" :key="j" class="ev"
                   :style="{ left: d.xPct + '%', background: d.color }"
                   @mousemove="tipAt($event, d.title)" @mouseleave="hideTip"></div>
            </div>
          </div>
          <div class="viz-axis" v-if="swimGeo.lanes.length"><span>0 s</span><span>{{ swimGeo.mid }} s</span><span>{{ swimGeo.max }} s</span></div>
          <div v-else class="viz-empty">Aucune session à afficher.</div>
        </div>

        <!-- ERREURS -->
        <div v-else-if="view === 'err'">
          <h2 class="viz-h2">Erreurs &amp; santé</h2>
          <div class="viz-kpis">
            <div class="kpi"><span class="v" :style="{color:C.err}">{{ errGeo.count }}</span><span class="l">Erreurs</span></div>
            <div class="kpi"><span class="v">{{ rows.length }}</span><span class="l">Total</span></div>
            <div class="kpi"><span class="v" :style="{color: errGeo.rate>5 ? C.err : '#37474F'}">{{ errGeo.rate }}%</span><span class="l">Taux d'erreur</span></div>
          </div>
          <div class="viz-grid2">
            <div>
              <div class="viz-blocklabel">Top routes en échec</div>
              <div v-if="errGeo.topRoutes.length">
                <div v-for="(r, i) in errGeo.topRoutes" :key="i" class="viz-hbar" @mousemove="tipAt($event, r.label+' — '+r.count+' erreurs')" @mouseleave="hideTip">
                  <span class="hb-lab">{{ r.short }}</span>
                  <span class="hb-track"><span class="hb-fill" :style="{ width: r.barW + '%', background: C.err }"></span></span>
                  <span class="hb-val">{{ r.count }}</span>
                </div>
              </div>
              <div v-else class="viz-empty sm">Aucune erreur 🎉</div>
            </div>
            <div>
              <div class="viz-blocklabel">Erreurs dans le temps</div>
              <div class="viz-svgwrap">
                <svg :width="errGeo.chart.w" :height="errGeo.chart.h">
                  <rect v-for="(b, i) in errGeo.chart.bars" :key="i" :x="b.x" :y="b.y" :width="b.w" :height="b.h" :fill="C.err" rx="2"
                        @mousemove="tipAt($event, b.count + ' erreur' + (b.count>1?'s':'') + ' · ' + b.label)" @mouseleave="hideTip"></rect>
                </svg>
              </div>
            </div>
          </div>
          <div class="viz-blocklabel" style="margin-top:14px">Dernières erreurs</div>
          <div class="viz-list">
            <div v-for="(e, i) in errGeo.recent" :key="i" class="viz-li">
              <span class="t">{{ e.clock }}</span>
              <span class="sc" :style="{ background: e.scolor }">{{ e.statut || '—' }}</span>
              <span class="rt">{{ e.route }}</span>
              <span class="msg">{{ e.msg }}</span>
            </div>
            <div v-if="!errGeo.recent.length" class="viz-empty sm">—</div>
          </div>
        </div>

        <!-- PERFORMANCES -->
        <div v-else-if="view === 'perf'">
          <h2 class="viz-h2">Performances (latence)</h2>
          <p class="viz-sub">Par route : barre = <b>p95</b> (les requêtes les plus lentes), trait = <b>médiane</b>. Triées par p95 décroissant.</p>
          <div v-if="perfGeo.rows.length">
            <div class="viz-perfhead"><span class="ph-lab"></span><span class="ph-scale">0</span><span class="ph-scale r">{{ perfGeo.max }} ms</span></div>
            <div v-for="(r, i) in perfGeo.rows" :key="i" class="viz-perf" @mousemove="tipAt($event, r.label+'<br>médiane '+r.median+' ms · p95 '+r.p95+' ms · '+r.count+' appels')" @mouseleave="hideTip">
              <span class="pf-lab">{{ r.short }}</span>
              <span class="pf-track">
                <span class="pf-fill" :style="{ width: r.barW + '%', background: r.p95 > 3000 ? C.err : (r.p95 > 1000 ? C.selection : C.api) }"></span>
                <span class="pf-med" :style="{ left: r.medX + '%' }" title="médiane"></span>
              </span>
              <span class="pf-val">{{ r.p95 }} ms</span>
            </div>
          </div>
          <div v-else class="viz-empty">Aucune durée exploitable dans les logs affichés.</div>
        </div>

        <!-- ACTIVITÉ -->
        <div v-else-if="view === 'act'">
          <h2 class="viz-h2">Activité dans le temps</h2>
          <div class="viz-controls">
            <div class="viz-seg">
              <button :class="{ on: actMode === 'type' }" @click="actMode = 'type'">par type</button>
              <button :class="{ on: actMode === 'statut' }" @click="actMode = 'statut'">par statut</button>
            </div>
            <div class="viz-legend2">
              <span v-for="(c, i) in actGeo.legend" :key="i" class="it"><i class="dot" :style="{ background: c.color }"></i>{{ c.label }}</span>
            </div>
          </div>
          <div v-if="actGeo.bars.length" class="viz-svgwrap">
            <svg :width="actGeo.w" :height="actGeo.h">
              <g v-for="(bar, i) in actGeo.bars" :key="i"
                 @mousemove="tipAt($event, bar.title)" @mouseleave="hideTip">
                <rect v-for="(s, j) in bar.segs" :key="j" :x="bar.x" :y="s.y" :width="actGeo.barW" :height="s.h" :fill="s.color"></rect>
                <rect :x="bar.x" :y="0" :width="actGeo.barW" :height="actGeo.h - 16" fill="transparent"></rect>
                <text :x="bar.x + actGeo.barW/2" :y="actGeo.h - 4" text-anchor="middle" class="viz-axlab" v-if="bar.tick">{{ bar.tick }}</text>
              </g>
            </svg>
          </div>
          <div v-else class="viz-empty">Pas de données temporelles.</div>
        </div>

        <!-- ENTONNOIR & TOPS -->
        <div v-else-if="view === 'funnel'">
          <h2 class="viz-h2">Entonnoir &amp; classements</h2>
          <div class="viz-blocklabel">Entonnoir du parcours type</div>
          <div v-if="funnelGeo.any" class="viz-funnel">
            <div v-for="(s, i) in funnelGeo.steps" :key="i" class="fn-row">
              <span class="fn-lab">{{ s.label }}</span>
              <span class="fn-track"><span class="fn-fill" :style="{ width: s.pct + '%', background: C.api }">{{ s.count }}</span></span>
              <span class="fn-drop" v-if="i > 0" :style="{ color: s.drop > 0 ? C.err : '#90A4AE' }">{{ s.drop > 0 ? '−' + s.drop + '%' : '—' }}</span>
            </div>
          </div>
          <div v-else class="viz-note">
            Aucune étape ne correspond aux logs affichés. Les étapes du parcours type se configurent dans
            <code>FUNNEL_STEPS</code> (en haut du composant) — adapte les routes à ton app.
          </div>

          <div class="viz-grid2" style="margin-top:16px">
            <div>
              <div class="viz-blocklabel">Top utilisateurs</div>
              <div v-for="(t, i) in tops.users" :key="i" class="viz-hbar">
                <span class="hb-lab">{{ t.short }}</span>
                <span class="hb-track"><span class="hb-fill" :style="{ width: t.barW + '%', background: C.api }"></span></span>
                <span class="hb-val">{{ t.count }}</span>
              </div>
              <div v-if="!tops.users.length" class="viz-empty sm">—</div>
            </div>
            <div>
              <div class="viz-blocklabel">Top routes</div>
              <div v-for="(t, i) in tops.routes" :key="i" class="viz-hbar">
                <span class="hb-lab">{{ t.short }}</span>
                <span class="hb-track"><span class="hb-fill" :style="{ width: t.barW + '%', background: C.page }"></span></span>
                <span class="hb-val">{{ t.count }}</span>
              </div>
              <div v-if="!tops.routes.length" class="viz-empty sm">—</div>
            </div>
            <div>
              <div class="viz-blocklabel">Top IP</div>
              <div v-for="(t, i) in tops.ips" :key="i" class="viz-hbar">
                <span class="hb-lab">{{ t.short }}</span>
                <span class="hb-track"><span class="hb-fill" :style="{ width: t.barW + '%', background: '#7E57C2' }"></span></span>
                <span class="hb-val">{{ t.count }}</span>
              </div>
              <div v-if="!tops.ips.length" class="viz-empty sm">—</div>
            </div>
            <div>
              <div class="viz-blocklabel">Répartition par type</div>
              <div v-for="(t, i) in tops.types" :key="i" class="viz-hbar">
                <span class="hb-lab"><i class="dot" :style="{ background: typeColor(t.key) }"></i>{{ t.key }}</span>
                <span class="hb-track"><span class="hb-fill" :style="{ width: t.barW + '%', background: typeColor(t.key) }"></span></span>
                <span class="hb-val">{{ t.count }}</span>
              </div>
              <div v-if="!tops.types.length" class="viz-empty sm">—</div>
            </div>
          </div>
        </div>
      </section>
    </div>

    <!-- tooltip -->
    <div class="viz-tip" :style="{ left: tip.x + 'px', top: tip.y + 'px', opacity: tip.show ? 1 : 0 }" v-html="tip.html"></div>
  </v-card>
</template>

<script>
import moment from "moment";

// ── Étapes du parcours type (entonnoir). ÉDITE ces routes selon ton app. ──
// Chaque étape = un libellé + un test (regex) sur la route/requête.
const FUNNEL_STEPS = [
  { label: "Accueil", rx: /accueil|\/$|home/i },
  { label: "Liste vélos", rx: /velos|catalogue|list/i },
  { label: "Détail vélo", rx: /velo\/|detail|produit/i },
  { label: "Réservation", rx: /reservation|reserv|contract/i },
  { label: "Paiement", rx: /paiement|payment|\/pay/i },
  { label: "Confirmation", rx: /confirmation|confirm|success/i },
];

const COLORS = { page: "#26A69A", selection: "#FB8C00", api: "#5C6BC0", clic: "#FB8C00", err: "#EF5350", other: "#78909C" };
const DATE_FORMATS = [
  "MM/DD/YYYY HH:mm:ss", "MM/DD/YYYY HH:mm", "DD/MM/YYYY HH:mm:ss", "DD/MM/YYYY HH:mm",
  "YYYY-MM-DD HH:mm:ss", "YYYY-MM-DDTHH:mm:ss", "MM/DD/YYYY", "DD/MM/YYYY", "YYYY-MM-DD",
];

export default {
  name: "ModalVisualisation",
  props: {
    logs: { type: Array, default: () => [] },
    formatTs: { type: Function, default: (v) => v },
    live: { type: Boolean, default: false },
    height: { type: Number, default: 0 },
  },
  emits: ["close", "open-detail"],
  data() {
    return {
      C: COLORS,
      view: "timeline",
      selectedSessionId: null,
      hideApi: false,
      actMode: "type",
      fluxMode: "sankey",
      fluxSel: null,
      selStep: 0,
      hoverNode: null,
      tip: { show: false, x: 0, y: 0, html: "" },
    };
  },
  methods: {
    // ---- helpers bas niveau ----
    parseD(v) {
      if (v === undefined || v === null || v === "") return null;
      if (!isNaN(v) && String(v).trim() !== "") {
        let n = parseInt(v); if (n < 1e12) n = n * 1000;
        const d = new Date(n); if (!isNaN(d.getTime())) return d;
      }
      const m = moment(v, DATE_FORMATS, true); if (m.isValid()) return m.toDate();
      const m2 = moment(new Date(v)); return m2.isValid() ? m2.toDate() : null;
    },
    tsOf(l) { const d = this.parseD(l.horodatage); return d ? d.getTime() : null; },
    routeLabel(l) {
      let m = (l.request || "").toString().trim();
      if (m) {
        const mm = m.match(/^(GET|POST|PUT|PATCH|DELETE|OPTIONS|HEAD)\s+/i);
        let p = mm ? m.slice(mm[0].length) : m;
        p = p.split("?")[0].trim();
        return p || m;
      }
      if (l.path) { const h = l.path.toString().replace(/^https?:\/\/[^/]+/i, ""); return (h.split("?")[0] || l.path).toString(); }
      return l.type_evenement || "—";
    },
    shortLabel(s, n) { s = (s || "").toString(); n = n || 26; return s.length > n ? "…" + s.slice(-(n - 1)) : s; },
    typeColor(t) { return COLORS[(t || "").toString().toLowerCase()] || COLORS.other; },
    // Type/couleur d'une route (pour le Flux) : déduit du libellé de la route.
    routeType(r) {
      r = (r || "").toString().toLowerCase();
      if (/refus|erreur|error/.test(r)) return "err";
      if (/selection|sélection/.test(r)) return "selection";
      if (r === "/" || /page|reservation|confirmation|accueil/.test(r)) return "page";
      if (/\/api|\/list|get_|login|account/.test(r)) return "api";
      return "other";
    },
    routeColor(r) { return COLORS[this.routeType(r)] || COLORS.other; },
    statutColor(s) { s = parseInt(s); if (isNaN(s)) return COLORS.other; if (s < 300) return "#43A047"; if (s < 400) return "#5C6BC0"; if (s < 500) return "#FB8C00"; return COLORS.err; },
    isErr(l) {
      const s = parseInt(l.statut);
      const n = (l.niveau || "").toString().toUpperCase();
      return (!isNaN(s) && s >= 400) || n.indexOf("ERR") === 0 || n === "FATAL" || n === "CRITICAL" || (l.erreur_message && String(l.erreur_message).trim().length > 0);
    },
    userCode(u) { const raw = (u || "").toString().trim(); const i = raw.indexOf(" · "); return (i === -1 ? raw : raw.slice(0, i)).trim(); },
    ipOf(c) { let raw = (c || "").toString(); const i = raw.indexOf(" · "); if (i !== -1) raw = raw.slice(0, i); const mz = raw.indexOf("Mozilla"); if (mz > 0) raw = raw.slice(0, mz); return raw.split(",")[0].trim(); },
    percentile(values, p) { const a = values.filter((v) => typeof v === "number" && !isNaN(v)).sort((x, y) => x - y); if (!a.length) return 0; const idx = Math.min(a.length - 1, Math.max(0, Math.ceil(p * a.length) - 1)); return a[idx]; },
    fmtClockTs(ts) { return ts ? moment(ts).format("HH:mm:ss") : ""; },
    fmtBinLabel(ts) { return ts ? moment(ts).format("DD/MM HH:mm") : ""; },
    // ---- tooltip ----
    tipAt(ev, html) { let x = ev.clientX + 14, y = ev.clientY + 14; if (x > window.innerWidth - 220) x = ev.clientX - 220; this.tip = { show: true, x: x, y: y, html: html }; },
    hideTip() { this.tip.show = false; },
  },
  computed: {
    rows() {
      return (this.logs || []).map((l) => Object.assign({}, l, { _ts: this.tsOf(l), _route: this.routeLabel(l) }));
    },
    sessions() {
      const m = {};
      this.rows.forEach((r) => {
        const k = r.id_parcours || "(sans session)";
        if (!m[k]) m[k] = { id: k, user: "", events: [] };
        m[k].events.push(r);
        if (!m[k].user && r.user) m[k].user = r.user;
      });
      const arr = Object.keys(m).map((k) => m[k]);
      arr.forEach((s) => s.events.sort((a, b) => (a._ts || 0) - (b._ts || 0)));
      arr.sort((a, b) => (b.events[b.events.length - 1]._ts || 0) - (a.events[a.events.length - 1]._ts || 0));
      return arr;
    },
    sessionItems() {
      return this.sessions.map((s) => ({ id: s.id, label: String(s.id).slice(0, 14) + " · " + this.userCode(s.user) + " · " + s.events.length + " évts" }));
    },
    selectedSession() {
      if (!this.sessions.length) return null;
      return this.sessions.find((s) => s.id === this.selectedSessionId) || this.sessions[0];
    },
    tlSteps() {
      if (!this.selectedSession) return [];
      let evs = this.selectedSession.events.slice();
      if (this.hideApi) evs = evs.filter((e) => (e.type_evenement || "").toLowerCase() !== "api");
      // Affichage du PLUS RÉCENT en haut → on inverse l'ordre chronologique.
      evs.reverse();
      return evs.map((e, i) => {
        // La ligne au-dessus (i-1) est la plus récente : l'écart affiché est le
        // temps écoulé entre elle et la ligne (plus ancienne) courante.
        const prev = i > 0 ? evs[i - 1] : null;
        let gap = "";
        if (prev && e._ts && prev._ts) { const s = Math.round((prev._ts - e._ts) / 1000); gap = s > 0 ? s + " s" : "instantané"; }
        return {
          type: e.type_evenement || "—", color: this.typeColor(e.type_evenement), route: e._route,
          statut: e.statut, scolor: this.statutColor(e.statut), dur: e.duree_ms, isErr: this.isErr(e),
          clock: this.fmtClockTs(e._ts), gap: gap, raw: e,
        };
      });
    },
    selStepData() { return this.tlSteps[this.selStep] || null; },
    fluxGeo() {
      const t = (function (sessions, routeOf) {
        const edges = {}, nodeCount = {}, posSum = {}, posN = {};
        sessions.forEach((s) => {
          const seq = [];
          s.events.forEach((e) => { const n = routeOf(e); if (n && seq[seq.length - 1] !== n) seq.push(n); });
          seq.forEach((n, i) => { nodeCount[n] = (nodeCount[n] || 0) + 1; posSum[n] = (posSum[n] || 0) + i; posN[n] = (posN[n] || 0) + 1; if (i > 0) { const k = seq[i - 1] + "\u0001" + n; edges[k] = (edges[k] || 0) + 1; } });
        });
        return { edges, nodeCount, posSum, posN };
      })(this.sessions, (e) => e._route);

      let nodes = Object.keys(t.nodeCount).map((n) => ({ key: n, count: t.nodeCount[n], avg: t.posSum[n] / Math.max(1, t.posN[n]) }));
      nodes.sort((a, b) => b.count - a.count);
      nodes = nodes.slice(0, 14);
      const keep = {}; nodes.forEach((n) => (keep[n.key] = n));
      nodes.forEach((n) => (n.col = Math.round(n.avg)));
      const colVals = Array.from(new Set(nodes.map((n) => n.col))).sort((a, b) => a - b);
      const colIndex = {}; colVals.forEach((c, i) => (colIndex[c] = i));
      const byCol = {}; nodes.forEach((n) => { const ci = colIndex[n.col]; (byCol[ci] = byCol[ci] || []).push(n); });
      Object.keys(byCol).forEach((ci) => byCol[ci].sort((a, b) => b.count - a.count));
      let maxRows = 0; Object.keys(byCol).forEach((ci) => (maxRows = Math.max(maxRows, byCol[ci].length)));
      const colW = 172, boxW = 134, boxH = 34, rowH = 62, padX = 16, padY = 22;
      const totalH = maxRows * rowH;
      nodes.forEach((n) => {
        const ci = colIndex[n.col]; const list = byCol[ci]; const r = list.indexOf(n);
        n.x = padX + ci * colW;
        const startY = padY + (totalH - list.length * rowH) / 2;
        n.y = startY + r * rowH; n.w = boxW; n.h = boxH; n.short = this.shortLabel(n.key, 20);
      });
      let maxEdge = 1; Object.keys(t.edges).forEach((k) => { const p = k.split("\u0001"); if (keep[p[0]] && keep[p[1]]) maxEdge = Math.max(maxEdge, t.edges[k]); });
      const edges = [];
      Object.keys(t.edges).forEach((k) => {
        const p = k.split("\u0001"), a = keep[p[0]], b = keep[p[1]]; if (!a || !b) return;
        const x1 = a.x + boxW, y1 = a.y + boxH / 2, x2 = b.x, y2 = b.y + boxH / 2, mx = (x1 + x2) / 2;
        edges.push({ from: p[0], to: p[1], count: t.edges[k], w: 1.5 + (t.edges[k] / maxEdge) * 8, d: "M" + x1 + "," + y1 + " C" + mx + "," + y1 + " " + mx + "," + y2 + " " + x2 + "," + y2, mx: mx, my: (y1 + y2) / 2 - 5 });
      });
      return { nodes: nodes, edges: edges, width: padX * 2 + (colVals.length - 1) * colW + boxW, height: Math.max(120, padY * 2 + totalH) };
    },
    // Données de flux partagées par les 4 représentations.
    fluxData() {
      const edges = {}, nodeTot = {}, posSum = {}, posN = {};
      this.sessions.forEach((s) => {
        const seq = [];
        s.events.forEach((e) => { const n = e._route; if (n && seq[seq.length - 1] !== n) seq.push(n); });
        seq.forEach((n, i) => {
          nodeTot[n] = (nodeTot[n] || 0) + 1; posSum[n] = (posSum[n] || 0) + i; posN[n] = (posN[n] || 0) + 1;
          if (i > 0) { const k = seq[i - 1] + "\u0001" + n; edges[k] = (edges[k] || 0) + 1; }
        });
      });
      const edgeArr = Object.keys(edges).map((k) => { const p = k.split("\u0001"); return { from: p[0], to: p[1], count: edges[k] }; }).sort((a, b) => b.count - a.count);
      return { edges: edges, edgeArr: edgeArr, nodeTot: nodeTot, posSum: posSum, posN: posN, nodes: Object.keys(nodeTot) };
    },
    fluxTop() {
      const arr = this.fluxData.edgeArr.slice(0, 18);
      const max = arr.reduce((m, e) => Math.max(m, e.count), 1);
      return arr.map((e) => ({ from: e.from, to: e.to, count: e.count, fromShort: this.shortLabel(e.from, 22), toShort: this.shortLabel(e.to, 22), barW: Math.round(e.count / max * 100) }));
    },
    fluxDrill() {
      const d = this.fluxData;
      const nodesSorted = d.nodes.slice().sort((a, b) => d.nodeTot[b] - d.nodeTot[a]);
      const selected = (this.fluxSel && d.nodeTot[this.fluxSel] != null) ? this.fluxSel : (nodesSorted[0] || null);
      const before = [], after = [];
      d.edgeArr.forEach((e) => { if (e.to === selected) before.push({ r: e.from, count: e.count }); if (e.from === selected) after.push({ r: e.to, count: e.count }); });
      const bmax = before.reduce((m, x) => Math.max(m, x.count), 1), amax = after.reduce((m, x) => Math.max(m, x.count), 1);
      before.forEach((x) => x.barW = Math.round(x.count / bmax * 100));
      after.forEach((x) => x.barW = Math.round(x.count / amax * 100));
      return { nodesSorted: nodesSorted, selected: selected, before: before, after: after };
    },
    fluxMatrix() {
      const d = this.fluxData;
      const top = d.nodes.slice().sort((a, b) => d.nodeTot[b] - d.nodeTot[a]).slice(0, 16);
      const ord = top.sort((a, b) => (d.posSum[a] / d.posN[a]) - (d.posSum[b] / d.posN[b]));
      const max = d.edgeArr.reduce((m, e) => Math.max(m, e.count), 1);
      const rows = ord.map((from) => ({
        from: from,
        cells: ord.map((to) => {
          const c = d.edges[from + "\u0001" + to] || 0; const t = c / max;
          return { to: to, count: c, bg: c ? ("rgba(92,107,192," + (0.12 + t * 0.78).toFixed(2) + ")") : "transparent", fg: c ? (t > 0.55 ? "#fff" : "#5a6b7b") : "transparent" };
        }),
      }));
      return { ord: ord, rows: rows, max: max };
    },
    fluxSankey() {
      const d = this.fluxData;
      const top = d.nodes.slice().sort((a, b) => d.nodeTot[b] - d.nodeTot[a]).slice(0, 12);
      const keep = {}; top.forEach((n) => (keep[n] = true));
      const links = d.edgeArr.filter((l) => keep[l.from] && keep[l.to]);
      if (!top.length || !links.length) return { nodes: [], ribbons: [], width: 320, height: 200 };
      const withCol = top.map((n) => ({ n: n, col: Math.round(d.posSum[n] / d.posN[n]) }));
      const colVals = Array.from(new Set(withCol.map((x) => x.col))).sort((a, b) => a - b);
      const colIndex = {}; colVals.forEach((c, i) => (colIndex[c] = i));
      const byCol = {}; withCol.forEach((x) => { const ci = colIndex[x.col]; (byCol[ci] = byCol[ci] || []).push(x.n); });
      const thru = {}; top.forEach((n) => { let s = 0; links.forEach((l) => { if (l.from === n || l.to === n) s += l.count; }); thru[n] = Math.max(1, s); });
      const colW = colVals.length > 7 ? 150 : 205, boxW = 18, padX = 14, padY = 18, gap = 16, H = 460;
      let maxUnits = 1, maxNodes = 1;
      Object.keys(byCol).forEach((ci) => { let u = 0; byCol[ci].forEach((n) => (u += thru[n])); maxUnits = Math.max(maxUnits, u); maxNodes = Math.max(maxNodes, byCol[ci].length); });
      // Échelle BORNÉE : sur peu de sessions l'échelle brute devient énorme et
      // pousse les nœuds hors cadre (effet "vide/bug"). On la plafonne.
      const scale = Math.max(3, Math.min((H - padY * 2 - gap * (maxNodes - 1)) / maxUnits, 30));
      const pos = {};
      Object.keys(byCol).forEach((ciStr) => {
        const ci = +ciStr; const list = byCol[ci];
        let totalH = gap * (list.length - 1); list.forEach((n) => (totalH += thru[n] * scale));
        let y = padY + ((H - padY * 2) - totalH) / 2;
        list.forEach((n) => { const h = thru[n] * scale; pos[n] = { x: padX + ci * colW, y: y, h: h, inY: y, outY: y }; y += h + gap; });
      });
      links.sort((a, b) => pos[a.to].y - pos[b.to].y);
      const ribbons = [];
      links.forEach((l) => {
        const s = pos[l.from], t = pos[l.to]; const w = l.count * scale;
        const x1 = s.x + boxW, x2 = t.x, y1 = s.outY, y2 = t.inY; s.outY += w; t.inY += w;
        const mx = (x1 + x2) / 2;
        const dd = "M" + x1 + "," + y1 + " C" + mx + "," + y1 + " " + mx + "," + y2 + " " + x2 + "," + y2 +
          " L" + x2 + "," + (y2 + w) + " C" + mx + "," + (y2 + w) + " " + mx + "," + (y1 + w) + " " + x1 + "," + (y1 + w) + " Z";
        ribbons.push({ d: dd, color: this.routeColor(l.to), title: "<b>" + this.shortLabel(l.from, 30) + " → " + this.shortLabel(l.to, 30) + "</b><br>" + l.count + " passage" + (l.count > 1 ? "s" : "") });
      });
      const nodes = top.map((n) => { const p = pos[n]; return { x: p.x, y: p.y, w: boxW, h: Math.max(3, p.h), ty: p.y + Math.max(10, p.h / 2 + 3), short: this.shortLabel(n, 24), color: this.routeColor(n) }; });
      return { nodes: nodes, ribbons: ribbons, width: padX * 2 + (colVals.length - 1) * colW + boxW + 190, height: H };
    },
    swimGeo() {
      const ss = this.sessions.slice(0, 14);
      let maxSpan = 0;
      ss.forEach((s) => { const f = s.events[0]._ts, l = s.events[s.events.length - 1]._ts; if (f && l) maxSpan = Math.max(maxSpan, (l - f) / 1000); });
      const lanes = ss.map((s) => {
        const f = s.events[0]._ts;
        return {
          short: String(s.id).slice(0, 12), user: this.userCode(s.user) + " · " + s.events.length + " évts",
          dots: s.events.map((e) => ({
            xPct: maxSpan > 0 && f && e._ts ? Math.min(100, ((e._ts - f) / 1000 / maxSpan) * 100) : 0,
            color: this.isErr(e) ? COLORS.err : this.typeColor(e.type_evenement),
            title: "<b>" + (e.type_evenement || "—") + "</b> · " + this.fmtClockTs(e._ts) + "<br>" + this.shortLabel(e._route, 40),
          })),
        };
      });
      return { lanes: lanes, max: Math.round(maxSpan), mid: Math.round(maxSpan / 2) };
    },
    errGeo() {
      const errs = this.rows.filter((r) => this.isErr(r));
      const total = this.rows.length || 1;
      const byRoute = {}; errs.forEach((e) => { byRoute[e._route] = (byRoute[e._route] || 0) + 1; });
      const topRoutes = Object.keys(byRoute).map((k) => ({ label: k, short: this.shortLabel(k, 30), count: byRoute[k] })).sort((a, b) => b.count - a.count).slice(0, 10);
      const maxR = topRoutes.reduce((m, r) => Math.max(m, r.count), 1);
      topRoutes.forEach((r) => (r.barW = Math.round((r.count / maxR) * 100)));
      // série temporelle
      const W = 420, H = 130, N = 24, pad = 6;
      const tsList = errs.map((e) => e._ts).filter((t) => t != null);
      const bars = [];
      if (tsList.length) {
        const allTs = this.rows.map((r) => r._ts).filter((t) => t != null);
        const min = Math.min.apply(null, allTs), max = Math.max.apply(null, allTs), step = Math.max(1, (max - min) / N);
        const counts = new Array(N).fill(0);
        errs.forEach((e) => { if (e._ts == null) return; let b = Math.floor((e._ts - min) / step); if (b >= N) b = N - 1; if (b < 0) b = 0; counts[b]++; });
        const maxC = Math.max.apply(null, counts) || 1; const bw = (W - pad * 2) / N;
        for (let i = 0; i < N; i++) { const h = Math.round((counts[i] / maxC) * (H - 20)); bars.push({ x: pad + i * bw + 1, y: H - 16 - h, w: Math.max(1, bw - 2), h: h, count: counts[i], label: this.fmtBinLabel(min + i * step) }); }
      }
      const recent = errs.slice().sort((a, b) => (b._ts || 0) - (a._ts || 0)).slice(0, 12).map((e) => ({
        clock: this.fmtClockTs(e._ts), statut: e.statut, scolor: this.statutColor(e.statut),
        route: this.shortLabel(e._route, 40), msg: (e.erreur_message || e.code_fm || "").toString().slice(0, 60),
      }));
      return { count: errs.length, rate: Math.round((errs.length / total) * 1000) / 10, topRoutes: topRoutes, chart: { w: W, h: H, bars: bars }, recent: recent };
    },
    perfGeo() {
      const by = {};
      this.rows.forEach((r) => { const d = parseInt(r.duree_ms); if (isNaN(d)) return; (by[r._route] = by[r._route] || []).push(d); });
      let rows = Object.keys(by).map((k) => ({ label: k, short: this.shortLabel(k, 30), count: by[k].length, median: this.percentile(by[k], 0.5), p95: this.percentile(by[k], 0.95) }));
      rows = rows.filter((r) => r.count >= 1).sort((a, b) => b.p95 - a.p95).slice(0, 12);
      const max = rows.reduce((m, r) => Math.max(m, r.p95), 1);
      rows.forEach((r) => { r.barW = Math.round((r.p95 / max) * 100); r.medX = Math.round((r.median / max) * 100); });
      return { rows: rows, max: max };
    },
    actGeo() {
      const W = 640, H = 180, N = 24, pad = 4;
      const allTs = this.rows.map((r) => r._ts).filter((t) => t != null);
      if (!allTs.length) return { bars: [], legend: [], w: W, h: H, barW: 0 };
      const min = Math.min.apply(null, allTs), max = Math.max.apply(null, allTs), step = Math.max(1, (max - min) / N);
      let cats;
      if (this.actMode === "type") cats = [{ key: "page", color: COLORS.page }, { key: "selection", color: COLORS.selection }, { key: "api", color: COLORS.api }, { key: "autre", color: COLORS.other }];
      else cats = [{ key: "2xx", color: "#43A047" }, { key: "3xx", color: "#5C6BC0" }, { key: "4xx", color: "#FB8C00" }, { key: "5xx", color: COLORS.err }];
      const catOf = (r) => {
        if (this.actMode === "type") { const t = (r.type_evenement || "").toLowerCase(); return (t === "page" || t === "selection" || t === "api") ? t : "autre"; }
        const s = parseInt(r.statut); if (isNaN(s)) return "2xx"; if (s < 300) return "2xx"; if (s < 400) return "3xx"; if (s < 500) return "4xx"; return "5xx";
      };
      const grid = []; for (let i = 0; i < N; i++) { const o = { _total: 0 }; cats.forEach((c) => (o[c.key] = 0)); grid.push(o); }
      this.rows.forEach((r) => { if (r._ts == null) return; let b = Math.floor((r._ts - min) / step); if (b >= N) b = N - 1; if (b < 0) b = 0; grid[b][catOf(r)]++; grid[b]._total++; });
      const maxTot = grid.reduce((m, g) => Math.max(m, g._total), 1);
      const bw = (W - pad * 2) / N, plotH = H - 18;
      const bars = [];
      for (let i = 0; i < N; i++) {
        let yAcc = plotH; const segs = [];
        cats.forEach((c) => { const v = grid[i][c.key]; if (!v) return; const h = Math.round((v / maxTot) * plotH); yAcc -= h; segs.push({ y: yAcc, h: h, color: c.color, key: c.key, count: v }); });
        const title = this.fmtBinLabel(min + i * step) + "<br>" + grid[i]._total + " événements";
        bars.push({ x: pad + i * bw + 1, segs: segs, title: title, tick: (i % 6 === 0) ? moment(min + i * step).format("HH:mm") : "" });
      }
      return { bars: bars, legend: cats.map((c) => ({ label: c.key, color: c.color })), w: W, h: H, barW: Math.max(1, bw - 2) };
    },
    funnelGeo() {
      const counts = FUNNEL_STEPS.map(() => 0);
      let any = false;
      this.sessions.forEach((s) => {
        const labels = s.events.map((e) => e._route);
        let reached = -1, cursor = 0;
        for (let i = 0; i < labels.length; i++) { while (cursor < FUNNEL_STEPS.length && FUNNEL_STEPS[cursor].rx.test(labels[i] || "")) { reached = cursor; cursor++; } }
        for (let k = 0; k <= reached; k++) { counts[k]++; any = true; }
      });
      const base = counts[0] || 1;
      const steps = FUNNEL_STEPS.map((s, i) => ({ label: s.label, count: counts[i], pct: Math.round((counts[i] / base) * 100), drop: i > 0 ? Math.round(((counts[i - 1] - counts[i]) / (counts[i - 1] || 1)) * 100) : 0 }));
      return { any: any, steps: steps };
    },
    tops() {
      const agg = (keyFn) => { const m = {}; this.rows.forEach((r) => { const k = keyFn(r); if (!k) return; m[k] = (m[k] || 0) + 1; }); const arr = Object.keys(m).map((k) => ({ key: k, count: m[k] })).sort((a, b) => b.count - a.count).slice(0, 8); const mx = arr.reduce((x, r) => Math.max(x, r.count), 1); arr.forEach((r) => { r.barW = Math.round((r.count / mx) * 100); r.short = this.shortLabel(r.key, 26); }); return arr; };
      return { users: agg((r) => this.userCode(r.user)), routes: agg((r) => r._route), ips: agg((r) => this.ipOf(r.client)), types: agg((r) => (r.type_evenement || "").toString()) };
    },
  },
  watch: {
    sessions: { immediate: true, handler(v) { if (v && v.length && (this.selectedSessionId == null || !v.find((s) => s.id === this.selectedSessionId))) { this.selectedSessionId = v[0].id; this.selStep = 0; } } },
    selectedSessionId() { this.selStep = 0; },
  },
};
</script>

<style scoped>
.viz { display: flex; flex-direction: column; max-height: 100vh; overflow: hidden; border-radius: 12px; border: 1px solid #e3e8ee; }
.viz-live { display: inline-flex; align-items: center; gap: 6px; margin-left: 12px; font-size: 11px; font-weight: 800; letter-spacing: .04em; color: #fff; }
.viz-live .ld { width: 9px; height: 9px; border-radius: 50%; background: #69f0ae; box-shadow: 0 0 0 0 rgba(105,240,174,.7); animation: viz-pulse 1.6s infinite; }
@keyframes viz-pulse { 0% { box-shadow: 0 0 0 0 rgba(105,240,174,.6); } 70% { box-shadow: 0 0 0 7px rgba(105,240,174,0); } 100% { box-shadow: 0 0 0 0 rgba(105,240,174,0); } }
.viz-head { display: flex; align-items: center; gap: 4px; background: linear-gradient(135deg, #5c6bc0, #3949ab); color: #fff; padding: 12px 16px; }
.viz-head-title { font-weight: 800; font-size: 16px; }
.viz-head-sub { font-size: 12px; opacity: .85; margin-left: 10px; }
.viz-body { display: flex; flex: 1; min-height: 0; }
.viz-nav { flex: 0 0 196px; border-right: 1px solid #e3e8ee; background: #fafbfc; padding: 8px; overflow-y: auto; }
.viz-navgroup { font-size: 10.5px; text-transform: uppercase; letter-spacing: .05em; color: #90a4ae; font-weight: 800; padding: 10px 8px 4px; }
.viz-navitem { display: flex; align-items: center; gap: 8px; width: 100%; text-align: left; border: none; background: transparent; color: #455a64; font-size: 12.5px; font-weight: 600; padding: 8px 9px; border-radius: 9px; cursor: pointer; }
.viz-navitem:hover { background: #eef1f6; }
.viz-navitem.on { background: #5c6bc0; color: #fff; }
.viz-content { flex: 1; min-width: 0; overflow-y: scroll; overflow-x: auto; padding: 16px 18px; scrollbar-width: thin !important; scrollbar-color: #b0bec5 #eceff1 !important; }
/* Barre de défilement verticale VISIBLE (le CSS global de l'app les masque) */
.viz-content::-webkit-scrollbar { width: 12px !important; height: 12px !important; background: #eceff1 !important; display: block !important; }
.viz-content::-webkit-scrollbar-thumb { background: #b0bec5 !important; border-radius: 6px !important; border: 2px solid #eceff1 !important; }
.viz-content::-webkit-scrollbar-thumb:hover { background: #90a4ae !important; }
/* idem pour la liste de navigation à gauche */
.viz-nav { scrollbar-width: thin !important; }
.viz-nav::-webkit-scrollbar { width: 10px !important; background: #eef1f6 !important; display: block !important; }
.viz-nav::-webkit-scrollbar-thumb { background: #cfd8e0 !important; border-radius: 5px !important; border: 2px solid #eef1f6 !important; }
.viz-h2 { font-size: 16px; margin: 0 0 2px; color: #16202b; }
.viz-sub { font-size: 12.5px; color: #5a6b7b; margin: 0 0 14px; }
.viz-legend, .viz-legend2 { display: flex; gap: 14px; flex-wrap: wrap; align-items: center; font-size: 11.5px; color: #5a6b7b; margin-bottom: 14px; }
.viz-legend .it, .viz-legend2 .it { display: flex; align-items: center; gap: 6px; font-weight: 600; }
.viz-legend .muted { color: #90a4ae; font-weight: 500; }
.dot { width: 11px; height: 11px; border-radius: 4px; display: inline-block; }
.viz-cards { display: grid; grid-template-columns: repeat(auto-fill, minmax(230px, 1fr)); gap: 12px; }
.viz-card { text-align: left; border: 1px solid #e3e8ee; border-radius: 13px; background: #fff; padding: 14px; cursor: pointer; transition: all .12s; }
.viz-card:hover { border-color: #5c6bc0; transform: translateY(-2px); box-shadow: 0 6px 16px rgba(20,32,43,.08); }
.viz-card .ic { width: 40px; height: 40px; border-radius: 11px; display: flex; align-items: center; justify-content: center; margin-bottom: 9px; }
.viz-card b { display: block; font-size: 14px; color: #16202b; margin-bottom: 3px; }
.viz-card small { color: #5a6b7b; font-size: 12px; line-height: 1.4; }
.viz-controls { display: flex; gap: 14px; align-items: center; flex-wrap: wrap; margin-bottom: 14px; }
.viz-toggle { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #5a6b7b; font-weight: 600; cursor: pointer; }
.viz-toggle input { accent-color: #5c6bc0; width: 15px; height: 15px; }
.viz-svgwrap { overflow-x: auto; overflow-y: hidden; border: 1px solid #eef1f5; border-radius: 10px; padding: 8px; background: #fcfdfe; scrollbar-width: auto !important; scrollbar-color: #90a4ae #eceff1 !important; }
/* Barre de défilement horizontale VISIBLE (le CSS global de l'app masque les scrollbars) */
.viz-svgwrap::-webkit-scrollbar { height: 12px !important; width: 12px !important; background: #eceff1 !important; display: block !important; }
.viz-svgwrap::-webkit-scrollbar-thumb { background: #90a4ae !important; border-radius: 6px !important; border: 2px solid #eceff1 !important; }
.viz-svgwrap::-webkit-scrollbar-thumb:hover { background: #607d8b !important; }
.viz-empty { color: #90a4ae; font-size: 13px; padding: 24px; text-align: center; }
.viz-empty.sm { padding: 10px; font-size: 12px; }
.viz-note { font-size: 12.5px; color: #5a6b7b; background: #f5f7fa; border: 1px dashed #d7dee6; border-radius: 10px; padding: 12px; }
.viz-note code { background: #eceff4; padding: 1px 5px; border-radius: 4px; }

/* timeline */
.viz-tl { position: relative; margin-left: 6px; padding-left: 26px; }
.viz-tl:before { content: ""; position: absolute; left: 7px; top: 6px; bottom: 6px; width: 2px; background: #e3e8ee; }

/* Vue Timeline : le défilement se fait UNIQUEMENT sur la liste (l'en-tête et
   le sélecteur de session restent fixes). */
.viz-content-tl { display: flex; flex-direction: column; overflow: hidden !important; }
.viz-content-tl .viz-tlwrap { display: flex; flex-direction: column; flex: 1; min-height: 0; }
.viz-content-tl .viz-tlwrap .viz-tl {
  flex: 1; min-height: 0; overflow-y: scroll; overflow-x: hidden; padding-right: 8px;
  scrollbar-width: thin !important; scrollbar-color: #b0bec5 #eceff1 !important;
}
.viz-content-tl .viz-tlwrap .viz-tl::-webkit-scrollbar { width: 12px !important; background: #eceff1 !important; display: block !important; }
.viz-content-tl .viz-tlwrap .viz-tl::-webkit-scrollbar-thumb { background: #b0bec5 !important; border-radius: 6px !important; border: 2px solid #eceff1 !important; }
.viz-content-tl .viz-tlwrap .viz-tl::-webkit-scrollbar-thumb:hover { background: #90a4ae !important; }
.viz-step { position: relative; margin-bottom: 4px; padding: 9px 11px; border: 1px solid #e3e8ee; border-radius: 11px; background: #f7f9fb; cursor: pointer; transition: all .12s; }
.viz-step:hover { border-color: #5c6bc0; }
.viz-step.sel { border-color: #5c6bc0; box-shadow: 0 0 0 2px rgba(92,107,192,.18); }
.viz-step .nd { position: absolute; left: -25px; top: 12px; width: 16px; height: 16px; border-radius: 50%; border: 3px solid #fff; box-shadow: 0 0 0 1px #e3e8ee; }
.viz-step .r1 { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.viz-step .tt { font-size: 10px; font-weight: 800; text-transform: uppercase; color: #fff; padding: 2px 7px; border-radius: 6px; }
.viz-step .rt { font-family: Menlo, Consolas, monospace; font-size: 12.5px; font-weight: 600; color: #16202b; }
.viz-step .sc { font-weight: 800; font-size: 11px; padding: 1px 6px; border-radius: 5px; color: #fff; }
.viz-step .errtag { color: #ef5350; font-size: 11px; font-weight: 700; }
.viz-step .r2 { display: flex; gap: 12px; margin-top: 3px; font-size: 11px; color: #90a4ae; }
.viz-gap { font-size: 10.5px; color: #90a4ae; margin: 2px 0 2px 2px; }
.viz-detail { margin-top: 12px; border: 1px dashed #d7dee6; border-radius: 11px; padding: 12px; font-size: 12.5px; }
.viz-detail .k { color: #90a4ae; font-weight: 600; width: 110px; display: inline-block; }
.viz-detail b { color: #16202b; }

/* flux */
.viz-edge { fill: none; stroke: #5c6bc0; opacity: .4; transition: opacity .12s; }
.viz-edge:hover { opacity: .85; }
.viz-ecnt { fill: #90a4ae; font-size: 10px; font-weight: 700; pointer-events: none; }
.viz-gnode { cursor: default; }
.viz-gnode rect { fill: #fff; stroke: #cfd8e0; stroke-width: 1.5; }
.viz-gnode:hover rect { stroke: #5c6bc0; stroke-width: 2; }
.viz-gnode.dim { opacity: .25; }
.viz-gnode .t1 { fill: #16202b; font-size: 11px; font-weight: 700; font-family: Menlo, Consolas, monospace; }
.viz-gnode .t2 { fill: #90a4ae; font-size: 10px; }

/* flux : sankey */
.viz-ribbon { opacity: .4; transition: opacity .12s; }
.viz-ribbon:hover { opacity: .78; }
.viz-sknode { fill: #16202b; font-size: 10.5px; font-weight: 700; font-family: Menlo, Consolas, monospace; }
/* flux : top transitions */
.viz-tr { display: flex; align-items: center; gap: 10px; margin-bottom: 6px; font-size: 12.5px; }
.viz-tr .pair { flex: 0 0 430px; width: 430px; display: flex; align-items: center; gap: 6px; overflow: hidden; }
.viz-tr .cd { width: 9px; height: 9px; border-radius: 3px; flex: 0 0 9px; }
.viz-tr .rl { font-family: Menlo, Consolas, monospace; font-size: 11.5px; color: #16202b; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 185px; }
.viz-tr .ar { color: #90a4ae; }
.viz-tr .tk { flex: 1; height: 14px; background: #eef1f5; border-radius: 7px; overflow: hidden; }
.viz-tr .fl { display: block; height: 100%; border-radius: 7px; }
.viz-tr .vv { flex: 0 0 40px; text-align: right; font-weight: 800; color: #16202b; }
/* flux : drilldown */
.viz-ddpick { display: flex; flex-wrap: wrap; gap: 7px; margin-bottom: 14px; }
.viz-ddchip { border: 1px solid #e3e8ee; background: #f7f9fb; color: #5a6b7b; padding: 5px 11px; border-radius: 16px; font-size: 11px; font-weight: 700; cursor: pointer; font-family: Menlo, Consolas, monospace; }
.viz-ddchip:hover { border-color: #5c6bc0; }
.viz-ddchip.on { background: #5c6bc0; color: #fff; border-color: transparent; }
.viz-ddgrid { display: grid; grid-template-columns: 1fr 52px 1fr; gap: 12px; align-items: start; }
.viz-ddcenter { display: flex; justify-content: center; padding-top: 22px; }
.viz-ddnode { background: #5c6bc0; color: #fff; border-radius: 10px; padding: 10px 6px; font-size: 10.5px; font-weight: 800; text-align: center; font-family: Menlo, Consolas, monospace; word-break: break-all; }
.viz-ddrow { display: flex; align-items: center; gap: 7px; margin-bottom: 5px; font-size: 12px; }
.viz-ddrow .cd { width: 9px; height: 9px; border-radius: 3px; flex: 0 0 9px; }
.viz-ddrow .rl { flex: 1; font-family: Menlo, Consolas, monospace; font-size: 11px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.viz-ddrow .bar { flex: 0 0 70px; height: 11px; background: #eef1f5; border-radius: 6px; overflow: hidden; }
.viz-ddrow .bar i { display: block; height: 100%; }
.viz-ddrow .vv { flex: 0 0 26px; text-align: right; font-weight: 700; }
/* flux : matrice */
table.viz-mx { border-collapse: collapse; font-size: 11px; }
table.viz-mx th, table.viz-mx td { border: 1px solid #e3e8ee; padding: 0; }
table.viz-mx th.col { writing-mode: vertical-rl; transform: rotate(180deg); height: 130px; white-space: nowrap; padding: 4px 2px; font-family: Menlo, Consolas, monospace; font-size: 10px; color: #5a6b7b; background: #f7f9fb; }
table.viz-mx th.row { text-align: right; padding: 4px 8px; font-family: Menlo, Consolas, monospace; white-space: nowrap; color: #37474f; background: #f7f9fb; }
table.viz-mx th.corner { background: #fff; }
table.viz-mx .cell { width: 30px; height: 30px; display: flex; align-items: center; justify-content: center; font-weight: 700; }

/* swimlanes */
.viz-lane { display: flex; align-items: center; gap: 10px; margin-bottom: 9px; }
.viz-lane .lab { width: 160px; flex: 0 0 160px; font-size: 11px; }
.viz-lane .lab b { display: block; font-size: 12px; color: #16202b; font-family: Menlo, Consolas, monospace; }
.viz-lane .lab small { color: #90a4ae; }
.viz-lane .track { position: relative; flex: 1; height: 28px; border-left: 2px solid #cfd8e0; background: linear-gradient(90deg, #f3f6f9, transparent); border-radius: 0 8px 8px 0; }
.viz-lane .ev { position: absolute; top: 50%; transform: translate(-50%, -50%); width: 14px; height: 14px; border-radius: 50%; border: 2px solid #fff; cursor: pointer; transition: transform .1s; }
.viz-lane .ev:hover { transform: translate(-50%, -50%) scale(1.4); z-index: 3; }
.viz-axis { display: flex; justify-content: space-between; margin-left: 170px; font-size: 10.5px; color: #90a4ae; }

/* kpis */
.viz-kpis { display: flex; gap: 22px; margin-bottom: 16px; }
.viz-kpis .kpi { display: flex; flex-direction: column; }
.viz-kpis .v { font-size: 26px; font-weight: 800; line-height: 1; }
.viz-kpis .l { font-size: 11px; color: #90a4ae; text-transform: uppercase; letter-spacing: .04em; margin-top: 3px; }
.viz-grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
.viz-blocklabel { font-size: 11.5px; font-weight: 800; color: #607d8b; text-transform: uppercase; letter-spacing: .04em; margin-bottom: 8px; }

/* hbars */
.viz-hbar { display: flex; align-items: center; gap: 8px; margin-bottom: 5px; font-size: 12px; }
.viz-hbar .hb-lab { flex: 0 0 150px; width: 150px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-family: Menlo, Consolas, monospace; font-size: 11px; color: #37474f; display: flex; align-items: center; gap: 5px; }
.viz-hbar .hb-track { flex: 1; height: 13px; background: #eef1f5; border-radius: 7px; overflow: hidden; }
.viz-hbar .hb-fill { display: block; height: 100%; border-radius: 7px; }
.viz-hbar .hb-val { flex: 0 0 42px; text-align: right; font-weight: 700; color: #37474f; }

/* perf */
.viz-perfhead { display: flex; font-size: 10px; color: #90a4ae; margin-bottom: 4px; }
.viz-perfhead .ph-lab { flex: 0 0 160px; }
.viz-perfhead .ph-scale { flex: 1; }
.viz-perfhead .ph-scale.r { text-align: right; }
.viz-perf { display: flex; align-items: center; gap: 8px; margin-bottom: 5px; font-size: 12px; }
.viz-perf .pf-lab { flex: 0 0 160px; width: 160px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-family: Menlo, Consolas, monospace; font-size: 11px; color: #37474f; }
.viz-perf .pf-track { position: relative; flex: 1; height: 15px; background: #eef1f5; border-radius: 7px; }
.viz-perf .pf-fill { display: block; height: 100%; border-radius: 7px; }
.viz-perf .pf-med { position: absolute; top: -2px; width: 2px; height: 19px; background: #16202b; }
.viz-perf .pf-val { flex: 0 0 60px; text-align: right; font-weight: 700; color: #37474f; }

/* activité */
.viz-seg { display: inline-flex; border: 1px solid #cfd8e0; border-radius: 9px; overflow: hidden; }
.viz-seg button { border: none; background: #fff; color: #607d8b; font-size: 12px; font-weight: 700; padding: 6px 12px; cursor: pointer; }
.viz-seg button.on { background: #5c6bc0; color: #fff; }
.viz-axlab { fill: #90a4ae; font-size: 9.5px; }

/* list */
.viz-list { border: 1px solid #eef1f5; border-radius: 10px; overflow: hidden; }
.viz-li { display: flex; align-items: center; gap: 10px; padding: 7px 10px; font-size: 12px; border-bottom: 1px solid #f1f4f7; }
.viz-li:last-child { border-bottom: none; }
.viz-li .t { color: #90a4ae; flex: 0 0 64px; font-variant-numeric: tabular-nums; }
.viz-li .sc { font-weight: 800; font-size: 11px; padding: 1px 6px; border-radius: 5px; color: #fff; flex: 0 0 auto; }
.viz-li .rt { font-family: Menlo, Consolas, monospace; font-size: 11px; color: #37474f; flex: 0 0 240px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.viz-li .msg { color: #ef5350; font-size: 11px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

/* funnel */
.viz-funnel { }
.fn-row { display: flex; align-items: center; gap: 10px; margin-bottom: 6px; font-size: 12px; }
.fn-lab { flex: 0 0 120px; color: #37474f; font-weight: 600; }
.fn-track { flex: 1; height: 22px; background: #eef1f5; border-radius: 7px; overflow: hidden; }
.fn-fill { display: flex; align-items: center; justify-content: flex-end; height: 100%; color: #fff; font-weight: 800; font-size: 11px; padding-right: 8px; border-radius: 7px; min-width: 26px; }
.fn-drop { flex: 0 0 56px; text-align: right; font-weight: 700; font-size: 11px; }

.viz-tip { position: fixed; z-index: 3000; pointer-events: none; background: #16202b; color: #fff; padding: 7px 10px; border-radius: 8px; font-size: 11.5px; line-height: 1.35; box-shadow: 0 6px 20px rgba(0,0,0,.25); transition: opacity .1s; white-space: nowrap; }

@media (max-width: 760px) {
  .viz-nav { flex-basis: 150px; }
  .viz-grid2 { grid-template-columns: 1fr; }
  .viz-lane .lab { width: 110px; flex-basis: 110px; }
  .viz-axis { margin-left: 120px; }
}
</style>
